import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import CaretIcon from 'components/icons/CaretIcon';
import styles from './RecommendationPage.module.scss';
import { COVERAGE_TYPES, PlanSearchCriteria } from './types';

interface PlanTabsProps {
  activeType: PlanSearchCriteria['type'];
  onTypeChange: (type: PlanSearchCriteria['type']) => void;
}

const PlanTabs = ({ activeType, onTypeChange }: PlanTabsProps) => {
  const [isCoverageOpen, setIsCoverageOpen] = useState(false);
  const coverageTriggerRef = useRef<HTMLButtonElement>(null);
  const coverageListboxRef = useRef<HTMLUListElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isCoverageOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsCoverageOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isCoverageOpen]);

  const handleCoverageTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'ArrowDown' && !isCoverageOpen) {
        e.preventDefault();
        flushSync(() => setIsCoverageOpen(true));
        coverageListboxRef.current
          ?.querySelector<HTMLElement>('[role="option"]')
          ?.focus();
      }
    },
    [isCoverageOpen]
  );

  const handleCoverageListboxKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>('[role="option"]')
    );
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        items[(currentIndex + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        items[(currentIndex - 1 + items.length) % items.length]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        setIsCoverageOpen(false);
        coverageTriggerRef.current?.focus();
        break;
      case 'Home':
        e.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
    }
  };

  const activeCoverageTypeEntry =
    COVERAGE_TYPES[activeType as keyof typeof COVERAGE_TYPES];
  const activeCoverageType = activeCoverageTypeEntry
    ? `${activeCoverageTypeEntry.name} ${activeCoverageTypeEntry.altName}`
    : '';

  return (
    <div className={styles.planTabsContainer}>
      {/* Desktop */}
      <div className={styles.planTabs}>
        {Object.entries(COVERAGE_TYPES).map(([value, { name, altName }]) => (
          <button
            key={value}
            className={`${styles.planTab}${
              activeType === value ? ` ${styles.active}` : ''
            }`}
            onClick={() => onTypeChange(value as PlanSearchCriteria['type'])}
          >
            {name} {altName}
          </button>
        ))}
      </div>

      {/* Mobile */}
      <div className={styles.planTabsMobileDropdown} ref={dropdownRef}>
        <div className={styles.planTypeDropdown}>
          <button
            type="button"
            className={`${styles.planTypeButton} ${
              isCoverageOpen ? styles.open : ''
            }`}
            aria-haspopup="listbox"
            aria-expanded={isCoverageOpen}
            aria-controls="coverage-type-listbox"
            ref={coverageTriggerRef}
            onClick={() => setIsCoverageOpen((prev) => !prev)}
            onKeyDown={handleCoverageTriggerKeyDown}
          >
            <span>{activeCoverageType}</span>
            <CaretIcon
              direction={isCoverageOpen ? 'down' : 'up'}
              size="large"
              color="gray"
            />
          </button>
          {isCoverageOpen && (
            <ul
              id="coverage-type-listbox"
              ref={coverageListboxRef}
              role="listbox"
              aria-label="Coverage type"
              className={styles.planTypeList}
              onKeyDown={handleCoverageListboxKeyDown}
            >
              {Object.entries(COVERAGE_TYPES).map(
                ([value, { name, altName }]) => (
                  <li key={value} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={activeType === value}
                      className={`${styles.planTypeOption} ${
                        activeType === value ? styles.selected : ''
                      }`}
                      onClick={() => {
                        onTypeChange(value as PlanSearchCriteria['type']);
                        setIsCoverageOpen(false);
                      }}
                    >
                      {name} {altName}
                    </button>
                  </li>
                )
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanTabs;
